import {i18n} from "i18next";
import {ActivityHotelState} from "../../types-enums/activity-hotel";
import {isEmptyHotelResponse} from "../check/type-guards";
import {HotelResponse} from "../../api/model/activity-hotel";
import {getActivityPersonCount,} from "../getter/activities";
import {getDurationInDaysOrNights} from "../getter/common";
import {
    COMMA_SEPARATOR,
    generateGuestString,
    generateNumberOfNightsString,
    generateTranslatedActivityTypeString,
    WhereToDisplayActivityString
} from "./common";
import {DurationProps} from "../../types-enums/activity-common-properties";
import {getHotelClassNumberValueForCalculationRequest} from "../getter/hotel";

/**
 * Generates a string describing the duration of a hotel stay in terms of nights.
 *
 * @param {string} stayString - The initial string to be appended with hotel stay duration information.
 * @param {DurationProps} hotelStayDuration - The object containing the duration of the hotel stay.
 * @param {i18n} i18n - The internationalization object used for translating the "night" or "nights" string.
 * @returns {string} - The updated stay string that includes the number of nights and the appropriate translated term.
 */
const generateStayString = (
    stayString: string,
    hotelStayDuration: DurationProps,
    i18n: i18n
): string => {
    const numberOfNights = getDurationInDaysOrNights(hotelStayDuration, true)!!
    stayString += `${COMMA_SEPARATOR}${numberOfNights} `;
    stayString += generateNumberOfNightsString(numberOfNights, i18n)
    return stayString
}

/**
 * Generates a string representing the number of hotel guests in a localized format.
 *
 * @param {number} peopleCount - The number of people or guests.
 * @param {object} i18n - The internationalization object used for translations.
 * @returns {string} - A localized string that includes the activity type, a preposition, and the guest count appropriately translated.
 *                   -> Hotel 1 for 1 Person
 *
 */
export const generateHotelGuestsCountString = (
    peopleCount: number,
    i18n: i18n
): string => {
    const {t} = i18n
    return `${generateTranslatedActivityTypeString("hotel", i18n)} ${t("GENERAL.FOR")} ${peopleCount} ${generateGuestString(peopleCount, i18n)}`
}

/**
 * Generates a location string for a customer hotel activity, considering various details such as
 * hotel information, room category, hotel class, and stay duration. This function is adaptable
 * for different display contexts (e.g., journey PDFs, travel components, dropdown journey) and can
 * create output in a single line or separate fields.
 *
 * @param {ActivityHotelState} hotelActivity - The state of the hotel activity to extract relevant details, including
 *     the selected hotel, city, room category, number of rooms, and stay details.
 * @param {i18n} i18n - The internationalization object used to retrieve language-specific strings.
 * @param {boolean} onOneLine - Determines whether the generated location string should be combined on a single line.
 * @param {boolean} isForJourneyPdf - Indicates if the string is being prepared specifically for a journey PDF.
 * @param {WhereToDisplayActivityString} [whereToDisplay] - Optional parameter indicating the context in which
 *     the output string will be displayed (e.g., travel components, PDFs, dropdowns).
 * @returns {{locationString: string, returnStayString: string|undefined, separator: string}} An object containing:
 *     - `locationString`: The final formatted string to represent hotel location and stay details.
 *     - `returnStayString`: The formatted string for the stay details.
 *     - `separator`: The separator used between elements of the strings.
 *     -> {"locationString":"Sofitel Legend Old Cataract Aswan, Aswan, EGY, 5 *, 1 room, 1 night","separator":", "}
 */
export const generateCustomerHotelLocationString = (
    hotelActivity: ActivityHotelState,
    i18n: i18n,
    onOneLine: boolean,
    isForJourneyPdf: boolean,
    whereToDisplay?: WhereToDisplayActivityString
): { locationString: string; returnStayString: string | undefined; separator: string; } => {
    const {t} = i18n
    const {hotel, city, hotelClass, roomCategory, numberOfRooms, hotelStayDuration} = hotelActivity
    let locationString, returnStayString, stayString = ''
    const hotelClassNumber = getHotelClassNumberValueForCalculationRequest(hotelClass)
    if (hotelClassNumber !== null && hotelClassNumber > 0) stayString += `${hotelClassNumber} ${t("GENERAL.ASTERIKS")}${COMMA_SEPARATOR}`;
    if (roomCategory) stayString += `${t(`SCREENS.HOTEL.ROOM_CATEGORY_${roomCategory}`)}${COMMA_SEPARATOR}`;
    if (numberOfRooms) {
        stayString += `${numberOfRooms} `;
        stayString += numberOfRooms === 1
            ? `${t("SCREENS.TRAVEL_COMPONENTS_OVERVIEW.ROOM")}`
            : `${t("SCREENS.TRAVEL_COMPONENTS_OVERVIEW.ROOMS")}`;
    }
    if (typeof whereToDisplay !== 'undefined') switch (whereToDisplay) {
        case WhereToDisplayActivityString.travelComponents:
            stayString = generateStayString(stayString, hotelStayDuration, i18n)
            break;
        case WhereToDisplayActivityString.pdf:
            stayString = generateStayString(stayString, hotelStayDuration, i18n)
            break;
        case WhereToDisplayActivityString.journey:
            break;
        case WhereToDisplayActivityString.dropdownJourney:
            stayString = generateStayString(stayString, hotelStayDuration, i18n)
            const personCount = getActivityPersonCount(hotelActivity)
            if (personCount !== null) stayString += `${COMMA_SEPARATOR}${personCount} ${generateGuestString(personCount, i18n)}`
            break;
        default:
            whereToDisplay satisfies never
    }

    if (hotel !== null && !isEmptyHotelResponse(hotel as HotelResponse)) {
        locationString = `${hotel.name}${COMMA_SEPARATOR}${hotel.city}${COMMA_SEPARATOR}${hotel.alpha3}`;
    } else {
        locationString = `${city?.name}${COMMA_SEPARATOR}${city?.alpha3}`;
    }

    if (onOneLine) {
        locationString = `${locationString}${COMMA_SEPARATOR}${stayString}`
    } else {
        returnStayString = `${stayString}`
    }

    if (isForJourneyPdf) {
        const {guestCount} = hotelActivity
        locationString = `${locationString}, ${generateHotelGuestsCountString(guestCount, i18n)}`
    }
    return {locationString, returnStayString, separator: COMMA_SEPARATOR}
}

